























































































































































































import MultiSelect from '@shared/components/forms/inputs/multi-select/multi-select.vue';
import EntitySelect from '@shared/components/forms/inputs/entity-select/entity-select.vue';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/en';
import 'vue2-datepicker/locale/nl';
import 'vue2-datepicker/locale/fr';
import 'vue2-datepicker/locale/es';
import { add, addYears, differenceInCalendarDays, isAfter } from 'date-fns';
import { ChartType, DisplayOptions } from '@shared/interfaces/chart-interfaces';
import { SelectOptionInterface } from '@shared/interfaces/inputs';
import ChartWrapper from '@shared/layouts/wrappers/chart-wrapper.vue';

import { chartBackgroundColor, filters, grouping } from '@/config/accesses_report';
import { ReportChartData, ReportData } from '@access-control/interfaces/accesses-report';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import BCChart from '@shared/components/charts/bc-chart.vue';
import Table from '@shared/components/tables/table.vue';
import { ChartData, ChartOptions } from 'chart.js';
import SkeletonBox from '@shared/components/mocks/skeleton-box/skeleton-box.vue';
import TableExportsToCSV from '@shared/components/tables/abstract-table/table-exports-to-csv';
import { mixins } from 'vue-class-component';
import DownloadPdfUsingTemplateMixin from '@shared/components/tables/abstract-table/download-pdf-using-template-mixing';
import ModulesHelper from '@core/services/modules.helper';

interface ReportFilters {
  period: string;
  date_from?: Date;
  date_to?: Date;
  analytic_account_ids?: number[];
  resource_ids?: number[];
}

@Component({
  name: 'bc-accesses-report-chart',
  components: {
    'bc-chart-wrapper': ChartWrapper,
    'bc-multi-select': MultiSelect,
    'date-picker': DatePicker,
    'bc-chart': BCChart,
    'bpc-table': Table,
    'bpc-entity-select': EntitySelect,
    'bpc-skeleton-box': SkeletonBox,
  },
})
export default class AccessesReportChart extends mixins(
  Vue,
  TableExportsToCSV,
  DownloadPdfUsingTemplateMixin,
) {
  declare $refs: TableExportsToCSV['$refs'] & {
    accessesChart: InstanceType<typeof BCChart> & {
      renderChart(chartData: ChartData, options?: ChartOptions): void;
    };
  };

  // Props
  @Prop({ type: String, default: 'this_week' }) readonly defaultPeriod: string | null;
  @Prop({
    type: Object,
    default: {
      showChartBar: false,
      showChartLine: false,
      showChartArea: false,
      showChartPie: true,
    },
  })
  readonly displayOptions: DisplayOptions | null;
  @Prop({ type: String }) readonly pageActionsPortalTarget: string;

  // Data
  showExportModal: boolean = false;
  showGeneratePDFModal: boolean = false;
  generating: boolean = false;
  defaultChartType: ChartType = ChartType.DoughnutChart;
  chartType: ChartType = ChartType.DoughnutChart;

  filters: ReportFilters = {
    period: '',
    date_from: null,
    date_to: null,
    analytic_account_ids: [],
    resource_ids: [],
  };

  grouping: string = null;

  reportData: ReportData = {
    report_data: [],
    total_sum: 0,
  };

  chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      fullWidth: true,
      position: 'top',
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          const label = data.labels[tooltipItem.index];
          const number = data.datasets[0].data[tooltipItem.index];
          return label + ': ' + number + ' ' + this.$t('reports.accesses_report.visits');
        },
      },
    },
  };

  loading: boolean = false;

  currentPage: number = 1;
  itemsPerPage: number = 15;

  chartData: ChartData = {
    labels: ['General account 1', 'General account 2', 'General account 3', 'General account 3'],
    datasets: [
      {
        label: 'My First Dataset',
        data: [300, 50, 100, 1000],
        backgroundColor: this.chartBackgroundColor,
      },
    ],
  };

  sortBy: string = 'total';
  sortDesc: boolean = true;

  // Computed
  get showDateRange(): boolean {
    return this.filters.period === 'custom';
  }

  get isTaxManagementModuleActivated(): boolean {
    return this.$modulesService.hasModuleActivated(ModulesHelper.TAX_MANAGEMENT_MODULE_NAME);
  }

  /**
   * Computes the sliced  report data.
   * If there are 20 or fewer records, returns the data without modification.
   * If there are more than 20 records, groups the top 20 records and aggregates the rest under the label 'Others'.
   * @returns {ReportChartData} The sliced report data.
   */
  get slicedReportData(): ReportChartData {
    // Check if there are less than or equal to 20 elements

    if (this.reportData?.report_data?.length <= 20) {
      // If there are 20 or fewer elements, return the data without modification
      return {
        labels: this.reportData.report_data.map(item => item.label),
        totals: this.reportData.report_data.map(item => item.total),
      };
    }

    // Sort the data by the 'totals' field in descending order
    const sortedReport = this.reportData.report_data.sort((a, b) => b.total - a.total);
    // Take the top 20 records
    const top20 = sortedReport.slice(0, 20);
    // Calculate the sum of the total for the remaining records
    const restTotal = sortedReport.slice(20).reduce((total, item) => total + item.total, 0);
    // Group the remaining records under the label 'Others'
    const others = {
      label: this.$t('reports.accesses_report.others') as string,
      total: restTotal,
    };
    // Return the data combining the top 20 and the 'Others' group
    return {
      labels: [...top20.map(item => item.label), others.label],
      totals: [...top20.map(item => item.total), others.total],
    };
  }

  get total(): number | null {
    if (this.reportData.total_sum) {
      return this.reportData.total_sum;
    }
    return 0;
  }

  get noResults(): boolean {
    return this.reportData.report_data.length === 0;
  }

  get tableFields() {
    let labelToShow = '';
    if (this.grouping) {
      labelToShow = this.$t(`reports.accesses_report.grouping.option.${this.grouping}`) as string;
    } else if (this.filters.period === 'custom') {
      const dateFrom = this.filters.date_from ? this.formatDate(this.filters.date_from) : '';
      const dateTo = this.filters.date_to ? this.formatDate(this.filters.date_to) : '';
      labelToShow =
        this.$t('reports.date_from.label') +
        ' ' +
        dateFrom +
        this.$t('reports.date_to.label') +
        ' ' +
        dateTo;
    } else {
      labelToShow = this.$t(`reports.period.option.${this.filters.period}`) as string;
    }

    const defaultTableFields = [
      { key: 'label', label: labelToShow, sortable: true },
      {
        key: 'total',
        label: this.$t('reports.report.total'),
        sortable: true,
        tdClass: 'text-right',
        thClass: 'text-right',
      },
    ];

    return defaultTableFields;
  }

  // Filters
  get periodFilterOptions(): SelectOptionInterface[] {
    return filters?.period?.options || [];
  }
  get periodFilterPlaceholder(): string {
    return this.$t(`${filters?.period?.placeholder}`).toString();
  }

  get resourceFilterPlaceholder(): string {
    return this.$t(`reports.accesses_report.resource`).toString();
  }
  get analitycAccountFilterPlaceholder(): string {
    return this.$t(`reports.accesses_report.analityc_account`).toString();
  }

  // Grouping
  get groupingOptions(): SelectOptionInterface[] {
    return grouping?.options;
  }

  get groupingPlaceholder(): string {
    return this.$t(`${grouping?.placeholder}`).toString();
  }
  get chartBackgroundColor(): any {
    return chartBackgroundColor;
  }

  get noLabel() {
    return {
      labelAttributes: {
        for: 'test',
      },
    };
  }

  get tableItems() {
    return this.reportData.report_data.map(saleReport => {
      const defaultTableItem = {
        label: saleReport.label,
        total: saleReport.total,
      };
      return defaultTableItem;
    });
  }
  get totalRows(): number {
    return this.reportData.report_data.length;
  }
  // Methods

  disableDateTo(date: Date): boolean {
    if (!this.filters.date_from) {
      return false;
    }
    const startDate = new Date(this.filters.date_from);
    const oneYearFromStartDate = addYears(startDate, 1);
    return isAfter(date, oneYearFromStartDate);
  }

  onDateFromChange(): void {
    if (this.filters.date_to) {
      const startDate = new Date(this.filters.date_from);
      const endDate = new Date(this.filters.date_to);
      const dayDiff = differenceInCalendarDays(endDate, startDate);
      if (dayDiff > 365) {
        const newEndDate = add(startDate, { days: 365 });
        this.filters.date_to = newEndDate;
      }
    }
  }

  onChartTypeChange(chartType: ChartType) {
    this.chartType = chartType;
    this.renderChart();
  }

  renderChart(): void {
    this.$nextTick().then(() => {
      this.chartData = {
        labels: this.slicedReportData.labels,
        datasets: [
          {
            label: this.$t('global.sales_report.totals') as string,
            data: this.slicedReportData.totals,
            backgroundColor: this.chartBackgroundColor,
          },
        ],
      };

      this.$refs.accessesChart.renderChart(this.chartData, this.chartOptions);
    });
  }

  async getReport() {
    this.loading = true;
    const url = this.$bpcApiRouteHelper.getEntityPath('accesses-report');
    const filters = this.filters;
    const grouping = this.grouping;
    const date_from = this.$bpcHelper.getDateDbStrV2(this.filters.date_from);
    const date_to = this.$bpcHelper.getDateDbStrV2(this.filters.date_to);
    this.$bpcApi
      .get(url, {
        params: {
          ...filters,
          date_from: date_from,
          date_to: date_to,
          grouping: grouping,
        },
      })
      .subscribe(res => {
        this.loading = false;
        this.reportData = res.data;
        this.sortBy = 'total';
        this.sortDesc = true;
        this.renderChart();
      });
  }

  onClickGeneratePDF() {
    this.generating = true;
    const url = this.$bpcApiRouteHelper.getEntityPath('accesses-report');
    const params = this.getQueryParams();
    this.$bpcApi.postDownload(`${url}/generate-pdf/`, '', { ...params }).subscribe(res => {
      this.generating = false;
    });
  }

  onClickExport() {
    this.showExportModal = true;
  }

  getQueryParams() {
    const filters = this.filters;
    const grouping = this.grouping;
    const orderBy = this.sortBy;
    const orderDirection = this.sortDesc ? 'desc' : 'asc';
    const date_from = this.$bpcHelper.getDateDbStrV2(this.filters.date_from);
    const date_to = this.$bpcHelper.getDateDbStrV2(this.filters.date_to);

    return {
      ...filters,
      date_from: date_from,
      date_to: date_to,
      grouping: grouping,
      order_by: orderBy,
      order_direction: orderDirection,
    };
  }

  formatDate(value): string {
    return this.$bpcHelper.formatDateV2(this.$bpcHelper.getDateDbStrV2(value));
  }

  moneyFormat(value) {
    return this.$bpcHelper.FRmoneyFormat(value);
  }

  // LIFECYCLE
  // created(){}
  mounted() {
    this.filters.period = this.defaultPeriod;
  }
  // updated(){}
  // destroyed(){}

  // Watchers
  @Watch('filters.period')
  onFilterPeriodChange(newVal: string, oldVal: string) {
    if (newVal === 'custom') {
      this.filters.date_from = null;
      this.filters.date_to = null;
    }

    if (newVal !== oldVal && newVal != 'custom') {
      this.getReport();
    }
  }

  @Watch('grouping')
  onFilterDurationChange(newVal: string, oldVal: string) {
    if (newVal !== oldVal) {
      this.getReport();
    }
  }
  @Watch('filters.date_from')
  onFilterDateFromChange(newVal: string, oldVal: string) {
    if (newVal && newVal !== oldVal) {
      if (this.filters.date_to) {
        this.getReport();
      }
    }
  }
  @Watch('filters.date_to')
  onFilterDateToChange(newVal: string, oldVal: string) {
    if (newVal && newVal !== oldVal) {
      if (this.filters.date_from) {
        this.getReport();
      }
    }
  }
  @Watch('filters.resource_ids')
  onFilterResourceChange(newVal: string, oldVal: string) {
    if (newVal && newVal !== oldVal) {
      this.getReport();
    }
  }
  @Watch('filters.analytic_account_ids')
  onFilterAnalyticAccountChange(newVal: string, oldVal: string) {
    if (newVal && newVal !== oldVal) {
      this.getReport();
    }
  }
}
